/**
 * Pure cosmetics-economy rules. The economy is **earned, cosmetics-only** — no
 * gameplay buffs, no real-money IAP, so the leaderboard stays fair
 * (docs/BARRAGE_IMPLEMENTATION_PLAN.md "Locked product decisions" §3). Tokens are
 * spent on stickers and wheel spins; the SERVER is the source of truth for every
 * balance (clients never debit their own wallet — §4). This module is just the
 * shared price/reward tables used by the Cloud Function (to charge/grant) and the
 * UI (to display a cost). Nothing here mutates state.
 *
 * Numbers are tuned from the legacy POPSTAR tables (AVOID_LEGACY_ANALYSIS.md §2.6
 * trade values, §2.7 wheels, §2.8 reward payouts) but normalised into clean,
 * monotonic-by-rarity tables instead of the legacy ad-hoc per-prize-code maps.
 *
 * NOTE: pure core module — never import Phaser, React, or Firebase here.
 */

/**
 * Sticker rarity tiers, cheapest → rarest. Drives shop pricing and (later) wheel
 * odds. Lives in core (not services) so both the server and the UI share one
 * definition and the data layer can reference it without core depending on services.
 */
export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

/** All rarities in ascending order — handy for iterating shop tabs / odds tables. */
export const RARITIES: readonly Rarity[] = [
  'common',
  'uncommon',
  'rare',
  'epic',
  'legendary',
] as const;

/**
 * Token shop price per rarity. Monotonic increasing with rarity. (Legacy only had
 * a flat secret-sticker *trade* value of 1–4 tokens by category, §2.6; the new
 * model adds a real buy-side curve so tokens have a meaningful sink.)
 */
export const STICKER_PRICE: Readonly<Record<Rarity, number>> = {
  common: 50,
  uncommon: 150,
  rare: 400,
  epic: 1000,
  legendary: 2500,
} as const;

/**
 * Tokens refunded when a *duplicate* sticker is sold/traded back. Mirrors the
 * legacy secret-sticker trade (§2.6) but scaled to the new price curve — always a
 * fraction of {@link STICKER_PRICE} so trading is a soft sink, not arbitrage.
 */
export const STICKER_SELL_VALUE: Readonly<Record<Rarity, number>> = {
  common: 10,
  uncommon: 30,
  rare: 80,
  epic: 200,
  legendary: 500,
} as const;

/** Known wheel identifiers (kept in sync with config/wheel docs in dataModel). */
export type WheelId = 'wonder' | 'tokenTrade';

/**
 * Token cost to spin each wheel. The daily **Wonder** wheel is free (gated by a
 * 24h server-timestamp cooldown instead, §2.7); the **token-trade** wheel costs
 * tokens per spin. The server enforces both cost and cooldown — these are display
 * values only.
 */
export const WHEEL_SPIN_COST: Readonly<Record<WheelId, number>> = {
  wonder: 0,
  tokenTrade: 100,
} as const;

/** Cooldown between free Wonder-wheel spins (ms) — legacy 24h gate (§2.7). */
export const WONDER_WHEEL_COOLDOWN_MS = 24 * 60 * 60 * 1000;

/**
 * The token price to buy a sticker of the given rarity directly from the shop.
 * The server enforces this transactionally on purchase (verify price + balance,
 * then debit + grant); the client only uses it to render the cost.
 *
 * @param rarity the sticker's rarity tier.
 * @returns the cost in tokens (a positive integer).
 */
export function stickerPrice(rarity: Rarity): number {
  return STICKER_PRICE[rarity];
}

/**
 * The token refund for selling/trading back a duplicate sticker of the given
 * rarity. Always less than {@link stickerPrice} for the same rarity.
 *
 * @param rarity the sticker's rarity tier.
 * @returns the refund in tokens (a positive integer).
 */
export function stickerSellValue(rarity: Rarity): number {
  return STICKER_SELL_VALUE[rarity];
}

/**
 * The token cost to spin the given wheel (0 for the free daily Wonder wheel).
 *
 * @param wheelId which wheel.
 * @returns the cost in tokens.
 */
export function wheelSpinCost(wheelId: WheelId): number {
  return WHEEL_SPIN_COST[wheelId];
}

/**
 * Whether the player can afford `cost` tokens given a `balance`. A trivial guard,
 * but centralised so the affordability check is identical on client (to disable a
 * button) and server (to reject a purchase) — the server remains authoritative.
 *
 * @param balance the player's current token balance.
 * @param cost the token cost being charged.
 * @returns true when `balance >= cost` and the cost is a sane non-negative number.
 */
export function canAfford(balance: number, cost: number): boolean {
  return Number.isFinite(balance) && Number.isFinite(cost) && cost >= 0 && balance >= cost;
}
